import React, { useState, useContext, useEffect, useCallback, useMemo } from "react";
import "./styles/ProjectDetails.css";
import { json, Link } from "react-router-dom";
import VariableContext from "../contexts/VariableContext";
import config from "../configs/config";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../providers/AuthProvider";

const ProjectDetails = ({ project, projectid, projectOwner }) => {
  const { user } = useAuth();

  const {
    banners,
    savedWeightings,
    setBanners,
    setSavedWeightings,
    surveyFileName,
    setSurveyFileName,
    surveyName,
    setSurveyName,
    setSdataGroup,
    dataGroup,
    sdataGroup,
    projectId,
    setdataGroup,
    setProjectId,
  } = useContext(VariableContext);
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showUsers, setShowUsers] = useState(false);
  const [replaceFileMode, setReplaceFileMode] = useState(false);
  const [selectedReplaceFile, setSelectedReplaceFile] = useState(null);
  const [replaceFileUploading, setReplaceFileUploading] = useState(false);
  const [uploadQnrMode, setUploadQnrMode] = useState(false);
  const [selectedQnrFile, setSelectedQnrFile] = useState(null);
  const [qnrUploading, setQnrUploading] = useState(false);

  // Add this state
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateResult, setUpdateResult] = useState(null);
  const [allowedFeatures, setAllowedFeatures] = useState([]);

  // NEW: Local state to track QNR filename (avoids reload for immediate UI updates)
  const [localQnrFilename, setLocalQnrFilename] = useState(null);

  // Memoize project data
  const memoizedProject = useMemo(() => project, [project]);

  // Memoize client and project data
  const {
    client,
    project: proj,
    filename,
    uploaded_time,
    qgroup_json,
    sqgroup_json,
  } = memoizedProject || {};

  // UPDATED: Use local-or-prop for QNR filename
  const qnrFilename = localQnrFilename || memoizedProject?.qnr_filename;

  // Memoize number of questions
  const numberOfQuestions = useMemo(() => {
    if (!memoizedProject) return 0;

    return memoizedProject.qgroup_json
      ? Object.keys(memoizedProject.qgroup_json).length
      : memoizedProject.sqgroup_json
      ? Object.keys(memoizedProject.sqgroup_json).length
      : 0;
  }, [memoizedProject]);

  // Move all hook calls to the top level (no conditionals)
  const fetchOfficeUsers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${config.API_BASE_URL}/office-users`);
      if (!response.ok) throw new Error("Failed to fetch users");
      const data = await response.json();
      setUsers(data);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleCheckboxChange = useCallback((email) => {
    setSelectedUsers((prev) =>
      prev.includes(email) ? prev.filter((e) => e !== email) : [...prev, email]
    );
  }, []);

  const handleReplaceFileChange = useCallback((e) => {
    setSelectedReplaceFile(e.target.files[0]);
  }, []);

  const handleQnrFileChange = useCallback((e) => {
    setSelectedQnrFile(e.target.files[0]);
  }, []);

  // UPDATED: Use local-or-prop qnrFilename in checks and body
  const handleUpdateFromScripting = useCallback(() => {
    // Check if we have SPSS data
    if (!dataGroup) {
      alert("Please load a project with SPSS data first");
      return;
    }

    // Check if we have a QNR file uploaded
    if (!qnrFilename) {
      alert('Please upload a QNR file first using the "Upload QNR" button');
      return;
    }

    setIsUpdating(true);
    setUpdateResult(null);

    fetch(`${config.API_BASE_URL}/project/${projectId}/validate_and_update`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        data_group: dataGroup, // This is qgroup_json from SPSS
        qnr_filename: qnrFilename, // UPDATED: Use local-or-prop
      }),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        return response.json();
      })
      .then((data) => {
        if (data.success) {
          // Update the dataGroup state with the validated data
          setdataGroup(data.updated_data_group);
          setUpdateResult({
            success: true,
            message: `Successfully updated ${data.stats.changed_variables} variables`,
            stats: data.stats,
            details: data.details,
          });

          // Optional: Show a success message
          alert(
            `Validation and update complete! ${data.stats.changed_variables} variables were updated.`
          );
        } else {
          setUpdateResult({
            success: false,
            message: data.error || "Update failed",
          });
          alert("Update failed: " + (data.error || "Unknown error"));
        }
      })
      .catch((error) => {
        console.error("Update error:", error);
        setUpdateResult({
          success: false,
          message: error.message,
        });
        alert("Error during update: " + error.message);
      })
      .finally(() => {
        setIsUpdating(false);
      });
  }, [dataGroup, qnrFilename, projectId, setdataGroup]); // UPDATED: Depend on qnrFilename

  const handleShareClick = useCallback(() => {
    setShowUsers(!showUsers);
    if (!showUsers) {
      fetchOfficeUsers();
    }
  }, [showUsers, fetchOfficeUsers]);

  const handleShareProject = useCallback(async () => {
    if (selectedUsers.length === 0) {
      return;
    }

    try {
      const response = await fetch(`${config.API_BASE_URL}/share-project`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          projectId: projectid,
          user_ids: selectedUsers,
          client_name: client?.name || "",
          project_name: proj?.name || "",
          user: projectOwner,
        }),
      });

      if (!response.ok) throw new Error("Failed to share project");
      const result = await response.json();
      alert("Project shared successfully!");
    } catch (error) {
      alert("Failed to share project. Please try again.");
    }
  }, [selectedUsers, projectid, client, proj, projectOwner]);

  const handleReplaceFile = useCallback(async () => {
    if (!selectedReplaceFile) {
      alert("Please select a file first");
      return;
    }

    setReplaceFileUploading(true);

    const formData = new FormData();
    formData.append("file", selectedReplaceFile);
    formData.append("projectId", projectid);
    formData.append("client", JSON.stringify(client || {}));
    formData.append("project", JSON.stringify(proj || {}));

    try {
      const response = await axios.post(
        `${config.API_BASE_URL}/replace-spss-file`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data.success) {
        alert("SPSS file replaced successfully!");
        // Refresh project data to show new filename and updated time
        window.location.reload();
      } else {
        alert("Error replacing file: " + response.data.message);
      }
    } catch (error) {
      console.error("Error replacing file:", error);
      alert("Error replacing file: " + error.message);
    } finally {
      setReplaceFileUploading(false);
      setReplaceFileMode(false);
      setSelectedReplaceFile(null);
    }
  }, [selectedReplaceFile, projectid, client, proj]);

  const handleScriptingClick = useCallback(
    (e) => {
      e.preventDefault();

      if (!memoizedProject) return;

      // Only use sqgroup_json data
      if (memoizedProject.sqgroup_json) {
        setSdataGroup(memoizedProject.sqgroup_json);
        setProjectId(projectid);

        setSurveyFileName(filename); // Store the original filename
        setSurveyName(filename?.replace(/\.[^/.]+$/, "") || "");
      }
      // Navigate to survey builder
      navigate("/surveybuilder");
    },
    [
      memoizedProject,
      projectid,
      filename,
      setSdataGroup,
      setProjectId,
      setSurveyFileName,
      setSurveyName,
      navigate,
    ]
  );

  // UPDATED: Set localQnrFilename after success (no reload)
  const handleUploadQnr = useCallback(async () => {
    if (!selectedQnrFile) {
      alert("Please select a QNR file first");
      return;
    }

    setQnrUploading(true);

    const formData = new FormData();
    formData.append("file", selectedQnrFile);
    formData.append("projectId", projectid);
    formData.append("client", JSON.stringify(client || {}));
    formData.append("project", JSON.stringify(proj || {}));

    try {
      const response = await axios.post(
        `${config.API_BASE_URL}/upload-qnr`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data.success) {
        alert("QNR file uploaded successfully!");

        // UPDATED: Update local state immediately for UI reactivity
        // (Assumes backend stores filename as uploaded file.name; adjust if response.data includes it)
        setLocalQnrFilename(selectedQnrFile.name);

        // Note: Parent can still refresh props later if needed, but local update activates validation instantly

        // Reset the upload state
        setUploadQnrMode(false);
        setSelectedQnrFile(null);

        // REMOVED: window.location.reload(); // No longer needed
      } else {
        alert("Error uploading QNR: " + response.data.message);
      }
    } catch (error) {
      console.error("Error uploading QNR:", error);
      alert("Error uploading QNR: " + error.message);
    } finally {
      setQnrUploading(false);
    }
  }, [selectedQnrFile, projectid, client, proj]);

  // Memoize user list rendering
  const userListContent = useMemo(() => {
    if (loading) {
      return <p>Loading users...</p>;
    }

    if (users.length > 0) {
      return (
        <form>
          {users.map((userItem, index) => (
            <div key={index} className="checkbox-item">
              <label>
                <input
                  type="checkbox"
                  value={userItem.email}
                  checked={selectedUsers.includes(userItem.mail)}
                  onChange={() => handleCheckboxChange(userItem.mail)}
                />
                {userItem.displayName}
              </label>
            </div>
          ))}
        </form>
      );
    }

    return <p>No users found in this organization.</p>;
  }, [loading, users, selectedUsers, handleCheckboxChange]);

  useEffect(() => {
    const organization = user?.user?.organization || user?.organization;

    if (
      organization &&
      organization.features &&
      Array.isArray(organization.features) &&
      organization.features.length > 0
    ) {
      setAllowedFeatures(organization.features);
    } else {
      // If no features OR empty array, allow all
      setAllowedFeatures([
        "scripting",
        "datacheck",
        "datatables",
        "banner",
        "weighting",
        "dataanalysis",
        "dashboardbuilder",
        "crosstab",
      ]);
    }
  }, [user]);

  useEffect(() => {
    // Load data into context when project changes
    if (memoizedProject) {
      if (memoizedProject.qgroup_json) {
        setdataGroup(memoizedProject.qgroup_json);
        // console.log(
        //   "✅ Loaded dataGroup:",
        //   Object.keys(memoizedProject.qgroup_json).length,
        //   "questions"
        // );
      }
      if (memoizedProject.sqgroup_json) {
        setSdataGroup(memoizedProject.sqgroup_json);
        // console.log(
        //   "✅ Loaded sdataGroup:",
        //   memoizedProject.sqgroup_json.length,
        //   "questions"
        // );
      }
      if (projectid) {
        setProjectId(projectid);
      }
    }
  }, [memoizedProject, projectid, setdataGroup, setSdataGroup, setProjectId]);

  if (!memoizedProject) {
    return (
      <div className="project-details">
        Please select a project to view its details.
      </div>
    );
  }

  const hasFeatureAccess = (feature) => {
    return allowedFeatures.includes(feature);
  };

  // UPDATED: Use qnrFilename (local-or-prop)
  const qnr_filename = qnrFilename;

  return (
    <div className="project-details">
      <h2>Project Details</h2>
      <div className="details-container">
        <div className="detail-item">
          <span className="detail-title">Project Owner:</span>
          <span className="detail-value">{projectOwner}</span>
        </div>
        <div className="detail-item">
          <span className="detail-title">Client Name:</span>
          <span className="detail-value">{client?.name || "N/A"}</span>
        </div>
        <div className="detail-item">
          <span className="detail-title">Project Name:</span>
          <span className="detail-value">{proj?.name || "N/A"}</span>
        </div>
        <div className="detail-item">
          <span className="detail-title">File Name:</span>
          <span className="detail-value">{filename || "N/A"}</span>
          {!replaceFileMode && (
            <button
              onClick={() => setReplaceFileMode(true)}
              className="replace-file-btn"
            >
              Replace SPSS File
            </button>
          )}
        </div>
        {replaceFileMode && (
          <div className="file-replace-section">
            <h4>Replace SPSS File (will maintain all configurations)</h4>
            <input
              type="file"
              onChange={handleReplaceFileChange}
              accept=".sav,.SAV"
            />
            <div className="file-replace-actions">
              <button
                onClick={handleReplaceFile}
                disabled={!selectedReplaceFile || replaceFileUploading}
              >
                {replaceFileUploading ? "Replacing..." : "Confirm Replace"}
              </button>
              <button
                onClick={() => {
                  setReplaceFileMode(false);
                  setSelectedReplaceFile(null);
                }}
                disabled={replaceFileUploading}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
        <div className="detail-item">
          <span className="detail-title">Upload Time:</span>
          <span className="detail-value">
            {uploaded_time ? new Date(uploaded_time).toLocaleString() : "N/A"}
          </span>
        </div>
        <div className="detail-item">
          <span className="detail-title">Number of Questions:</span>
          <span className="detail-value">{numberOfQuestions}</span>
        </div>
        <div className="detail-item">
          <span className="detail-title">Weighting:</span>
          <span className="detail-value">{savedWeightings ? "Yes" : "NA"}</span>
        </div>
        <div className="detail-item">
          <span className="detail-title">Banner:</span>
          <span className="detail-value">
            {banners ? banners.length : "NA"}
          </span>
        </div>
        <div className="detail-item">
          <span className="detail-title">QNR File:</span>
          {qnr_filename ? (
            <span className="detail-value">{qnr_filename}</span>
          ) : (
            <span className="detail-value">Not uploaded</span>
          )}
          {!uploadQnrMode ? (
            <button
              onClick={() => setUploadQnrMode(true)}
              className="upload-qnr-btn"
            >
              {qnr_filename ? "Replace QNR" : "Upload QNR"}{" "}
              {/* UPDATED: Use qnr_filename */}
            </button>
          ) : (
            <span className="detail-value">Upload in progress...</span>
          )}
        </div>
        {uploadQnrMode && (
          <div className="qnr-upload-section">
            <h4>Upload Questionnaire (Word, Excel, or PDF)</h4>
            <input
              type="file"
              onChange={handleQnrFileChange}
              accept=".doc,.docx,.xls,.xlsx,.pdf"
            />
            <div className="qnr-upload-actions">
              <button
                onClick={handleUploadQnr}
                disabled={!selectedQnrFile || qnrUploading}
              >
                {qnrUploading ? "Uploading..." : "Upload QNR"}
              </button>
              <button
                onClick={() => {
                  setUploadQnrMode(false);
                  setSelectedQnrFile(null);
                }}
                disabled={qnrUploading}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Data Validation Section - Updated to use QNR file */}
      {dataGroup && (
        <div
          style={{
            margin: "20px 0",
            padding: "10px",
            border: "1px solid #ccc",
            borderRadius: "5px",
          }}
        >
          <h4>Data Validation & Update</h4>
          <p style={{ fontSize: "0.9em", color: "#666", marginBottom: "10px" }}>
            Validate SPSS data against the uploaded QNR file
            {!qnr_filename && ( // UPDATED: Use qnr_filename
              <span style={{ color: "#dc3545", fontWeight: "bold" }}>
                {" "}
                (Please upload a QNR file first)
              </span>
            )}
          </p>
          <button
            onClick={handleUpdateFromScripting}
            disabled={isUpdating || !dataGroup || !qnr_filename} // UPDATED: Use qnr_filename
            style={{
              padding: "10px 20px",
              backgroundColor: !qnr_filename // UPDATED: Use qnr_filename
                ? "#6c757d"
                : isUpdating
                ? "#ccc"
                : "#4CAF50",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: !qnr_filename || isUpdating ? "not-allowed" : "pointer", // UPDATED: Use qnr_filename
              marginRight: "10px",
            }}
          >
            {isUpdating
              ? "Validating and Updating..."
              : "Validate & Update from QNR File"}
          </button>

          {updateResult && (
            <div
              style={{
                marginTop: "10px",
                padding: "10px",
                backgroundColor: updateResult.success ? "#d4edda" : "#f8d7da",
                border: `1px solid ${
                  updateResult.success ? "#c3e6cb" : "#f5c6cb"
                }`,
                borderRadius: "4px",
              }}
            >
              <strong>
                {updateResult.success ? "✓ Success:" : "✗ Error:"}
              </strong>{" "}
              {updateResult.message}
              {updateResult.stats && (
                <div style={{ marginTop: "5px", fontSize: "0.9em" }}>
                  <div>
                    Variables checked: {updateResult.stats.total_variables}
                  </div>
                  <div>
                    Variables updated: {updateResult.stats.changed_variables}
                  </div>
                  <div>
                    Variables unchanged:{" "}
                    {updateResult.stats.unchanged_variables}
                  </div>
                  {updateResult.details && updateResult.details.length > 0 && (
                    <details style={{ marginTop: "10px" }}>
                      <summary>View details of changes</summary>
                      <ul style={{ marginLeft: "20px", marginTop: "5px" }}>
                        {updateResult.details.map((detail, index) => (
                          <li key={index}>{detail}</li>
                        ))}
                      </ul>
                    </details>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Share button */}
      <div className="share-section">
        <button className="share-button" onClick={handleShareClick}>
          {showUsers ? "Hide Users" : "Show Users"}
        </button>
        {showUsers && selectedUsers && selectedUsers.length > 0 && (
          <button onClick={handleShareProject} style={{ marginTop: "10px" }}>
            Confirm Share
          </button>
        )}
        {showUsers && (
          <div className="user-list scrollable-container">
            <h4>Select Users to Share With:</h4>
            {userListContent}
          </div>
        )}
      </div>

      <div className="project-links">
        {hasFeatureAccess("scripting") && (
          <Link
            to="/surveybuilder"
            className="project-link"
            onClick={handleScriptingClick}
          >
            Scripting
          </Link>
        )}
        {hasFeatureAccess("datacheck") && (
          <Link to="/datacheck" className="project-link">
            DV
          </Link>
        )}
        {hasFeatureAccess("datatables") && (
          <Link to="/datatables" className="project-link">
            Data Tables
          </Link>
        )}
        {hasFeatureAccess("banner") && (
          <Link to="/banner" className="project-link">
            Banner
          </Link>
        )}
        {/* {hasFeatureAccess("weighting") && (
          <Link to="/weighting" className="project-link">
            Weighting
          </Link>
        )} */}
        {/* {hasFeatureAccess("dataanalysis") && (
          <Link to="/dataanalysis" className="project-link">
            Data Analysis
          </Link>
        )} */}
        {hasFeatureAccess("dashboardbuilder") && (
          <Link to="/dashboardbuilder" className="project-link">
            Dashboard
          </Link>
        )}
      </div>
      
    </div>
  );
};

export default ProjectDetails;